import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { TbEdit } from 'react-icons/tb';
import { RiDeleteBin6Line, RiStore2Line, RiFileCopyLine, RiCheckLine, RiExternalLinkLine } from 'react-icons/ri';
import TableSkeletonLoader from '../../../utils/TableSkeletonLoader';
import defaultImage from '../../../utils/DefaultImage';
import PaginationComponent from '../../../utils/PaginationComponent';
import ConfirmationDialog from '../../../utils/ConfirmationDialog';
import EditWineQuantityModal from './EditWineQuantityModal';
import viewIcon from "../../../assets/admin/view.svg";
import DashboardTooltip from "../Components/DashboardTooltip";
import ImagePreviewModal from "../Components/ImagePreviewModal";
import ProposalDetailsCard from './ProposalDetailsCard';

const ProposalPreview = (props) => {
    const { hasPermission } = props || {};
    const { slug2 } = useParams();
    const navigate = useNavigate();
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");

    const [proposal, setProposal] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    
    // Modal states
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [wineToDelete, setWineToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [wineToEdit, setWineToEdit] = useState(null);
    const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState(false);

    // 1-Click Clipboard copy for Wine Image URL
    const handleCopyImageUrl = async (url) => {
        if (!url || url === "null" || url === "N/A") return;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(String(url));
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = String(url);
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }
            setCopiedUrl(true);
            setTimeout(() => setCopiedUrl(false), 2000);
        } catch {
            // Silently fallback if clipboard access restricted
        }
    };

    // Pagination for wines
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchProposalDetails = useCallback(async () => {
        if (!slug2) return;
        setIsLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/api/v1/proposals/${slug2}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            // console.log("Proposal Details Response:", response.data);
            setProposal(response.data);
        } catch (error) {
            console.error("Error fetching proposal details:", error);
        } finally {
            setIsLoading(false);
        }
    }, [slug2, apiUrl, token]);

    useEffect(() => {
        fetchProposalDetails();
    }, [fetchProposalDetails]);

    // Clean image url in case of markdown formatting artifacts
    const cleanImageUrl = (url) => {
        if (!url || url === "null") return null;
        let clean = url;
        if (clean.startsWith("[")) {
            clean = clean.split('"]')[0].replace('["', '');
        }
        return clean;
    };

    // Delete wine handler
    const confirmDeleteWine = async () => {
        if (!wineToDelete) return;

        if (!proposal?.proposal_wines || proposal.proposal_wines.length <= 1) {
            toast.warning("Impossibile eliminare l'ultimo vino. La proposal deve contenere almeno un vino.", { theme: "colored" });
            setWineToDelete(null);
            return;
        }

        if (hasPermission && !hasPermission("Proposals", "remove")) {
            toast.error("Non hai i permessi per eliminare questo vino.", { theme: "colored" });
            setWineToDelete(null);
            return;
        }

        const productId = wineToDelete.wine_id || wineToDelete.wine?.id || wineToDelete.product_id;
        const targetProposalId = slug2 || proposal?.id;

        if (!productId || !targetProposalId) {
            toast.error("Identificativo del vino o della proposal non valido.", { theme: "colored" });
            setWineToDelete(null);
            return;
        }

        setIsDeleting(true);
        try {
            const response = await axios.delete(
                `${apiUrl}/api/v1/proposals/${targetProposalId}/wines/${productId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const successMsg = response.data?.message || "Vino rimosso con successo dalla proposal!";
            toast.success(successMsg, { theme: "colored" });
            setWineToDelete(null);

            // Adjust page if deleting last item on the page
            if (currentWines.length === 1 && page > 1) {
                setPage(prev => prev - 1);
            }

            await fetchProposalDetails();
        } catch (error) {
            console.error("Error removing wine from proposal:", error);
            const msg = error.response?.data?.message || "Errore durante l'eliminazione del vino.";
            toast.error(msg, { theme: "colored" });
        } finally {
            setIsDeleting(false);
        }
    };

    // Save wine quantity handler
    const handleSaveWineQuantity = async (wineItem, newQuantity) => {
        if (!wineItem) return;
        if (hasPermission && !hasPermission("Proposals", "edit")) {
            toast.error("Non hai i permessi per modificare questa proposal.", { theme: "colored" });
            setWineToEdit(null);
            return;
        }
        const targetProposalId = slug2 || proposal?.id;
        const productId = wineItem.wine_id || wineItem.wine?.id || wineItem.product_id;

        if (!targetProposalId || !productId) {
            toast.error("Identificativo del vino o della proposal non valido.", { theme: "colored" });
            return;
        }

        setIsUpdatingQuantity(true);
        try {
            const response = await axios.patch(
                `${apiUrl}/api/v1/proposals/${targetProposalId}/wines/${productId}`,
                { quantity: Number(newQuantity) },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const successMsg = response.data?.message || "Quantità aggiornata con successo!";
            toast.success(successMsg, { theme: "colored" });
            setWineToEdit(null);
            await fetchProposalDetails();
        } catch (error) {
            console.error("Error updating wine quantity:", error);
            const msg = error.response?.data?.message || "Errore durante l'aggiornamento della quantità.";
            toast.error(msg, { theme: "colored" });
        } finally {
            setIsUpdatingQuantity(false);
        }
    };

    // Paginate wines list
    const totalWines = proposal?.proposal_wines?.length || 0;
    const startIndex = (page - 1) * rowsPerPage;
    const currentWines = proposal?.proposal_wines?.slice(startIndex, startIndex + rowsPerPage) || [];

    // Client name and type (Lead / Account) directly from API
    const clientName = String(proposal?.display_client_name || "").replace(/\s*\(Lead\)$/i, "").trim();
    const clientType = String(proposal?.client_type || "").trim();

    return (
        <div className="w-full relative">
            <div className="flex sm:flex-row flex-col sm:justify-between sm:items-center items-start gap-4 mt-3 mb-5 w-full">
                <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold poppins-medium text-[#303030]">
                        Dettagli Proposal
                    </h3>
                    {Boolean(clientName) && (
                        <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-[#FAF3F4] border border-[#8B2433]/25 shadow-xs">
                            <RiStore2Line className="w-4 h-4 text-[#8B2433] shrink-0" />
                            <span 
                                className="text-sm md:text-base font-semibold poppins-medium text-[#8B2433] truncate max-w-[220px] sm:max-w-[360px] md:max-w-none"
                                title={clientName}
                            >
                                {clientName}
                            </span>
                            {Boolean(clientType) && (
                                <span className={`inline-flex items-center justify-center px-2.5 py-0.5 text-[11px] font-semibold tracking-wider uppercase rounded-full border shadow-2xs poppins-medium ${
                                    clientType.toLowerCase() === "lead"
                                        ? "bg-orange-50 text-orange-800 border-orange-200"
                                        : "bg-blue-50 text-blue-800 border-blue-200"
                                }`}>
                                    {clientType}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <button
                    onClick={() => navigate('/admin/proposals')}
                    className="rounded-full py-[6px] border-[2px] bg-[#fff] border-[#F2C3CA] px-5 text-[#8B2433] text-sm font-medium shadow-sm poppins-regular whitespace-nowrap shrink-0 hover:bg-[#8B2433] hover:text-white transition-colors cursor-pointer"
                >
                    Torna indietro
                </button>
            </div>

            {/* Proposal Metadata Summary Card */}
            <ProposalDetailsCard proposal={proposal} isLoading={isLoading} />
            
            <div className="w-auto dash-table-main h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] rounded-xl shadow overflow-hidden overflow-x-auto">
                <table className="md:w-full min-w-[800px] text-left wine_table">
                    <thead>
                        <tr className="border-b-2 border-[#8B2433]/30 bg-[#FAF3F4]">
                            <th className="py-3.5 px-3 first:pl-6 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Sr.no</th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Immagine</th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Nome Vino</th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Quantità</th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Prezzo (B2B)</th>
                            <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Data</th>
                            <th className="py-3.5 px-3 last:pr-6 text-center poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Azione</th>
                        </tr>
                    </thead>
                    {isLoading ? (
                        <TableSkeletonLoader
                            columns={7}
                            rows={rowsPerPage || 10}
                            minWidths={["60px", "80px", "220px", "90px", "110px", "130px", "120px"]}
                            actionCount={3}
                        />
                    ) : (
                        <tbody>
                            {currentWines && currentWines.length > 0 ? (
                                currentWines.map((item, index) => (
                                    <tr key={item.id} className="border-b border-[#8B2433]/12 hover:bg-[#8B2433]/[0.03] transition-colors duration-150">
                                        <td className="py-4 px-3 first:pl-6 text-left poppins-medium text-[#303030]">
                                            {startIndex + index + 1}
                                        </td>
                                        <td className="pr-2 py-4 px-3">
                                            <div 
                                                className="thumbnail_image rounded-lg w-[50px] h-[60px] bg-white p-1 flex align-center justify-center cursor-pointer hover:shadow-md transition-shadow"
                                                onClick={() => setSelectedImage(cleanImageUrl(item?.wine?.image) || defaultImage)}
                                            >
                                                <img
                                                    className="w-full p-1 h-full object-contain border border-[#68172394] rounded-lg"
                                                    src={cleanImageUrl(item?.wine?.image) || defaultImage}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = defaultImage;
                                                    }}
                                                    alt="Wine"
                                                />
                                            </div>
                                        </td>
                                        <td className="py-4 px-3 text-left poppins-medium text-[#303030] text-sm">
                                            <DashboardTooltip text={item?.wine?.name || "N/A"} maxLength={25} />
                                        </td>
                                        <td className="py-4 px-3 text-left poppins-medium whitespace-nowrap">
                                            <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 text-xs font-semibold rounded-full bg-[#8B2433]/10 text-[#8B2433]">
                                                {item.quantity}
                                            </span>
                                        </td>
                                        <td className="py-4 px-3 text-left poppins-medium text-[#303030]">
                                            € {item.b2b_price_snapshot}
                                        </td>
                                        <td className="py-4 px-3 text-left poppins-medium text-[#303030] whitespace-nowrap">
                                            <span 
                                                className="text-gray-500 font-mono text-xs"
                                                title={item?.created_at ? dayjs(item?.created_at).format("DD-MM-YYYY HH:mm") : ""}
                                            >
                                                {item?.created_at ? dayjs(item?.created_at).format("DD-MM-YYYY") : "N/A"}
                                            </span>
                                        </td>
                                        <td className="py-4 px-3 last:pr-6 text-center poppins-medium text-[#303030]">
                                            <div className="flex items-center justify-center gap-2">
                                                {(Boolean(hasPermission?.("Proposals", "view")) || !hasPermission) && (
                                                    <button
                                                        onClick={() => setSelectedDetails(item)}
                                                        className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all flex items-center justify-center"
                                                        title="Vedi Dettagli Vino"
                                                    >
                                                        <img src={viewIcon} alt="view details" className="w-5 h-5" />
                                                    </button>
                                                )}
                                                {Boolean(hasPermission?.("Proposals", "edit")) && (
                                                    <button
                                                        onClick={() => setWineToEdit(item)}
                                                        className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all flex items-center justify-center"
                                                        title="Modifica Quantità Vino"
                                                    >
                                                        <TbEdit size={"20px"} className="text-[#8B2433]" />
                                                    </button>
                                                )}
                                                {Boolean(hasPermission?.("Proposals", "remove")) && (
                                                    <button
                                                        onClick={() => setWineToDelete(item)}
                                                        className="cursor-pointer font-medium text-[#8B2433] p-1.5 rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all flex items-center justify-center"
                                                        title="Elimina vino dalla proposal"
                                                    >
                                                        <RiDeleteBin6Line className="w-5 h-5 text-[#8B2433]" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="text-center py-4">Nessun vino trovato</td>
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
            </div>
            {proposal?.proposal_wines && proposal.proposal_wines.length > 0 && (
                <PaginationComponent
                    page={page}
                    totalPages={totalWines}
                    rowsPerPage={rowsPerPage}
                    setPage={setPage}
                    setRowsPerPage={setRowsPerPage}
                />
            )}

            {/* Common Image Preview Modal */}
            <ImagePreviewModal
                imageUrl={selectedImage}
                onClose={() => setSelectedImage(null)}
            />

            {/* Details Modal */}
            {selectedDetails && (
                <div 
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" 
                    onClick={() => setSelectedDetails(null)}
                >
                    <div 
                        className="bg-white rounded-2xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-xl overflow-hidden border border-[#8B2433]/20 transform transition-all" 
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="bg-[#8B2433] px-4 md:px-6 py-4 flex justify-between items-center text-white shrink-0">
                            <h3 className="text-xl font-semibold poppins-medium">Dettagli Vino</h3>
                            <button 
                                className="text-white hover:text-gray-200 text-2xl font-bold leading-none" 
                                onClick={() => setSelectedDetails(null)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="p-4 md:p-6 flex flex-col gap-5 overflow-y-auto premium-scrollbar">
                            <div className="border-b pb-4 poppins-regular text-center md:text-left shrink-0">
                                <h4 className="text-2xl md:text-3xl font-semibold text-[#8B2433] poppins-medium">{selectedDetails?.wine?.name || 'N/A'}</h4>
                                <p className="text-xs md:text-sm text-gray-500 font-medium mt-1 uppercase tracking-wider">{selectedDetails?.wine?.producer || 'Nessun produttore'}</p>
                            </div>
                            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                                <div className="w-full md:w-1/3 flex shrink-0">
                                    <div className="bg-gradient-to-br from-white via-[#FCFAFA] to-[#FAF2F4]/60 hover:bg-[#FAF3F4]/60 p-4 rounded-xl border border-[#8B2433]/20 shadow-xs hover:border-[#8B2433] hover:shadow-[0_4px_20px_rgba(139,36,51,0.14)] w-full h-full flex justify-center items-center min-h-[200px] md:min-h-[300px] transition-all duration-300 ease-out group cursor-default">
                                        <img 
                                            src={cleanImageUrl(selectedDetails?.wine?.image) || defaultImage} 
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = defaultImage;
                                            }}
                                            alt="Wine" 
                                            className="max-h-48 md:max-h-64 object-contain rounded-lg mix-blend-multiply transition-transform group-hover:scale-105 duration-300"
                                        />
                                    </div>
                                </div>
                                <div className="w-full md:w-2/3 flex flex-col poppins-regular">
                                    <style>{`
                                        .premium-scrollbar::-webkit-scrollbar { width: 6px; }
                                        .premium-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
                                        .premium-scrollbar::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 10px; }
                                        .premium-scrollbar::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
                                    `}</style>
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-3 text-sm p-1.5 md:pr-2.5 overflow-y-visible md:overflow-y-auto md:max-h-[45vh] premium-scrollbar">
                                        {[
                                            { label: "Tipologia", value: selectedDetails?.wine?.product_type },
                                            { label: "Nazione", value: selectedDetails?.wine?.nation },
                                            { label: "Vitigno", value: selectedDetails?.wine?.grape_variety },
                                            { label: "Riferimento", value: selectedDetails?.wine?.reference },
                                            { label: "Box Content", value: selectedDetails?.wine?.box_content },
                                            { label: "Tipo", value: selectedDetails?.wine?.type },
                                            { label: "Annata", value: selectedDetails?.wine?.vintage_year },
                                            { label: "Formato", value: selectedDetails?.wine?.bottle_format },
                                            { label: "Etichetta", value: selectedDetails?.wine?.label },
                                            { label: "Temp. Servizio", value: selectedDetails?.wine?.serving_temperature },
                                            { label: "Grad. Alcolica", value: selectedDetails?.wine?.alcoholic_strength },
                                            { label: "EAN13", value: selectedDetails?.wine?.ean13 },
                                            { label: "UPC", value: selectedDetails?.wine?.upc },
                                            { label: "Categoria", value: selectedDetails?.wine?.category },
                                            { label: "Territorio", value: selectedDetails?.wine?.territory },
                                            { label: "Cru", value: selectedDetails?.wine?.cru },
                                            { label: "Terreno", value: selectedDetails?.wine?.soil_geology },
                                            { label: "Tipo Impianto", value: selectedDetails?.wine?.plant_type },
                                            { label: "Prod. Annua", value: selectedDetails?.wine?.annual_production },
                                            { label: "Resa", value: selectedDetails?.wine?.yield },
                                            { label: "Affinamento", value: selectedDetails?.wine?.container_refinement },
                                            { label: "Fermentazione", value: selectedDetails?.wine?.fermentation },
                                            { label: "Invecchiamento", value: selectedDetails?.wine?.aging },
                                            { label: "Età Viti", value: selectedDetails?.wine?.age_of_vines },
                                            { label: "Lieviti", value: selectedDetails?.wine?.yeasts },
                                            { label: "Cultivar", value: selectedDetails?.wine?.cultivar },
                                            { label: "Filtrazione", value: selectedDetails?.wine?.filtration },
                                            { label: "Promozione", value: selectedDetails?.wine?.promotion },
                                            { label: "SO2", value: selectedDetails?.wine?.so2 },
                                            { label: "Disp. Netta", value: selectedDetails?.wine?.net_availability },
                                            { label: "Disp. Lorda", value: selectedDetails?.wine?.gross_availability },
                                            {
                                                label: "URL Immagine",
                                                value: cleanImageUrl(selectedDetails?.wine?.image) || selectedDetails?.wine?.image,
                                                isImageUrl: true
                                            }
                                        ].map((detail, idx) => {
                                            if (detail.isImageUrl) {
                                                const rawUrl = detail.value;
                                                const hasUrl = Boolean(rawUrl && rawUrl !== "null" && rawUrl !== "N/A");
                                                return (
                                                    <div key={idx} className="bg-gradient-to-br from-white via-[#FFFDFD] to-[#FAF1F3]/80 hover:bg-[#FAF3F4] p-2 rounded-lg border border-[#8B2433]/20 shadow-xs hover:border-[#8B2433] hover:shadow-[0_4px_16px_rgba(139,36,51,0.16)] hover:-translate-y-0.5 transition-all duration-200 ease-out flex flex-col justify-center col-span-2 group cursor-default">
                                                        <span className="text-[#8B2433]/70 group-hover:text-[#8B2433] transition-colors duration-200 block text-[10px] uppercase tracking-wider mb-0.5 font-semibold">
                                                            {detail.label}
                                                        </span>
                                                        {hasUrl ? (
                                                            <div className="flex items-center justify-between gap-1.5 min-w-0 w-full">
                                                                <span
                                                                    className="font-mono text-[11px] sm:text-xs font-medium text-gray-700 group-hover:text-[#6D1B27] transition-colors duration-200 tracking-tight overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0 !max-w-none"
                                                                    title={rawUrl}
                                                                >
                                                                    {rawUrl}
                                                                </span>
                                                                <div className="flex items-center gap-1 shrink-0">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCopyImageUrl(rawUrl)}
                                                                        title={copiedUrl ? "Copiato!" : "Copia URL Immagine"}
                                                                        className="p-1 rounded hover:bg-[#8B2433]/15 text-[#8B2433]/75 hover:text-[#8B2433] transition-colors cursor-pointer shrink-0 active:scale-95 bg-white border border-[#8B2433]/15"
                                                                    >
                                                                        {copiedUrl ? (
                                                                            <RiCheckLine className="w-3.5 h-3.5 text-emerald-600" />
                                                                        ) : (
                                                                            <RiFileCopyLine className="w-3.5 h-3.5" />
                                                                        )}
                                                                    </button>
                                                                    <a
                                                                        href={rawUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Apri immagine"
                                                                        className="p-1 rounded hover:bg-[#8B2433]/15 text-[#8B2433]/75 hover:text-[#8B2433] transition-colors shrink-0 bg-white border border-[#8B2433]/15"
                                                                    >
                                                                        <RiExternalLinkLine className="w-3.5 h-3.5" />
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="font-medium text-gray-400 text-xs">N/A</span>
                                                        )}
                                                    </div>
                                                );
                                            }

                                            const displayValue = (val) => {
                                                if (val === null || val === undefined || val === "") return "N/A";
                                                return val;
                                            };
                                            const valStr = displayValue(detail.value);
                                            return (
                                                <div key={idx} className="bg-gradient-to-br from-white via-[#FFFDFD] to-[#FAF1F3]/80 hover:bg-[#FAF3F4] p-2 rounded-lg border border-[#8B2433]/20 shadow-xs hover:border-[#8B2433] hover:shadow-[0_4px_16px_rgba(139,36,51,0.16)] hover:-translate-y-0.5 transition-all duration-200 ease-out flex flex-col justify-center group cursor-default">
                                                    <span className="text-[#8B2433]/70 group-hover:text-[#8B2433] transition-colors duration-200 block text-[10px] uppercase tracking-wider mb-0.5 font-semibold">{detail.label}</span>
                                                    <span className="font-medium text-gray-700 group-hover:text-[#6D1B27] transition-colors duration-200 text-xs truncate" title={valStr}>{valStr}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Wine Confirmation Modal */}
            <ConfirmationDialog
                isOpen={Boolean(wineToDelete)}
                title="Are you sure you want to delete the Wine?"
                onConfirm={confirmDeleteWine}
                onCancel={() => !isDeleting && setWineToDelete(null)}
                confirmText={isDeleting ? "Deleting..." : "Yes"}
                cancelText="No"
            />

            {/* Edit Wine Quantity Modal */}
            <EditWineQuantityModal
                isOpen={Boolean(wineToEdit)}
                wineItem={wineToEdit}
                onClose={() => !isUpdatingQuantity && setWineToEdit(null)}
                onSave={handleSaveWineQuantity}
                isSaving={isUpdatingQuantity}
            />
        </div>
    );
};

ProposalPreview.propTypes = {
    hasPermission: PropTypes.func,
};

export default ProposalPreview;
